import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    type CarouselApi,
} from '@rocco/components/carousel';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { formatToThousandsDelimiter } from '@rocco/utils/format-number';
import { cn } from '@rocco/utils/cn';
import { ScrollReveal } from '~/ui-rocco/pages/home-page/components/ScrollReveal';
import {
    BLEED_GUTTER_LEFT,
    CONTENT_CAP,
    useIslandSection,
    usePaginationAutoplay,
    useSectionReachesIsland,
} from '~/ui-rocco/pages/detail-shared';
import type { CategoryTileViewModel } from '@rocco/ui/tile-category/view-models/category';
import { useDirectoryBrowseTheme } from './directory-browse-theme';

interface DirectoryCategoryCarouselProps {
    /** Section heading, e.g. "Explore Product Categories". */
    title: string;
    categories: CategoryTileViewModel[];
    /** Noun for the count subtitle, e.g. "Suppliers", "Firms", "Projects". */
    countLabel: string;
}

/**
 * Top "explore categories" carousel for the directory browse pages. Each card is
 * a rounded category image with the category title and a count subtitle below
 * (no sub-category list, no overlaid title). Uses the Magic Island for
 * pagination + auto-slide — the same treatment as the detail-page carousels — so
 * the frosted island pill drives play/pause, dot-jump and dwell-timer advance.
 */
export const DirectoryCategoryCarousel = ({
    title,
    categories,
    countLabel,
}: DirectoryCategoryCarouselProps) => {
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    const [revealed, setRevealed] = useState(false);
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const { desktop } = useAppDisplayModeContext();
    // Matches how many cards fit the initial viewport: ~457px-basis cards on
    // desktop (4 across at 1920), a 70%-basis card plus a peek below md.
    const eagerTileCount = desktop ? 4 : 2;

    // One shared autoplay clock feeds either the floating island (desktop) or the
    // docked control (mobile). Below md the floating pill occluded the pill-bar
    // heading + Sort control mid-scroll, so on mobile we dock the same pause/dots
    // control statically under the carousel (inline flow — it occludes nothing),
    // and skip the island registration so the pill never floats there.
    // Gate the controls on embla actually having somewhere to scroll: a tile
    // count that fits one viewport (4 across at 1920) yields a single snap,
    // where pagination shows one dot that immediately reads as ended.
    const [scrollable, setScrollable] = useState(false);
    useEffect(() => {
        if (!emblaApi) return;
        const update = () =>
            setScrollable(emblaApi.scrollSnapList().length > 1);
        update();
        emblaApi.on('reInit', update);
        return () => {
            emblaApi.off('reInit', update);
        };
    }, [emblaApi]);
    const controlsEnabled = scrollable && revealed;
    const paginationMode = usePaginationAutoplay(emblaApi, { label: title });
    // The pill floats at the viewport bottom while the mid-viewport reading
    // line keeps this section active — which leaves it hovering over the pill
    // bar / grid once they scroll up under it. Release the island as soon as
    // the section's bottom edge clears the pill's spot, so it is gone just
    // before the filter bar reaches it.
    const sectionRef = useRef<HTMLElement | null>(null);
    const reachesIsland = useSectionReachesIsland(
        sectionRef,
        desktop && controlsEnabled,
    );
    const islandRef = useIslandSection<HTMLElement>(paginationMode, {
        enabled: desktop && controlsEnabled && reachesIsland,
        priority: 1,
    });
    const setSectionRef = useCallback(
        (node: HTMLElement | null) => {
            sectionRef.current = node;
            islandRef(node);
        },
        [islandRef],
    );

    if (categories.length === 0) {
        return null;
    }

    return (
        <section ref={setSectionRef} className="flex flex-col">
            <div className={cn(CONTENT_CAP, 'px-4 md:px-12')}>
                <hr
                    className={cn(
                        'm-0 border-t',
                        isDark ? 'border-white/15' : 'border-[#1f1f21]/15',
                    )}
                />
                <h2
                    className={cn(
                        // Mobile: small parenthesised label per the homepage
                        // editorial idiom; desktop keeps the display heading.
                        'pt-4 text-[0.9375rem] leading-[1.22] font-normal tracking-normal md:pt-5 md:text-[2rem] md:leading-[2.5rem] md:tracking-[-0.02rem]',
                        isDark ? 'text-white' : 'text-[#1f1f21]',
                    )}
                >
                    <span className="md:hidden">{`(${title})`}</span>
                    <span className="max-md:hidden">{title}</span>
                </h2>
            </div>
            {/* No reveal animation: the strip must paint on first load even
                when it sits at the fold. The wrapper remains purely as the
                in-view signal that flips `revealed` and starts the island
                autoplay. */}
            <ScrollReveal
                animate={false}
                threshold={0.2}
                onReveal={() => setRevealed(true)}
                className="pt-6"
            >
                <Carousel
                    setApi={setEmblaApi}
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        // A row that fits one viewport has nowhere to go —
                        // dragging it would only stretch the edge rubber band,
                        // so the grab (and with it wheel gestures and the
                        // overscroll spring) turns off with the controls.
                        // Options changing re-inits embla, so this settles on
                        // the first snap-list measurement.
                        watchDrag: scrollable,
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Desktop gutter lives on the embla viewport: snaps align
                        the first card to the heading's gutter (widening to the
                        centred 1920 container's gutter on >1920 screens) while
                        overflow clips at the padding box — so the strip runs
                        full-bleed to the viewport edge on the right and cards
                        stay visible through the gutter as they scroll out left.
                        Below md the padding moves onto the scrolling track
                        instead so exiting cards run to the viewport edge rather
                        than clipping at a 16px gutter. */}
                    <CarouselContent
                        className="max-md:gap-4 max-md:pl-4"
                        viewportClassName={BLEED_GUTTER_LEFT}
                    >
                        {categories.map((category, index) => (
                            <CarouselItem
                                key={category.id}
                                className="max-md:basis-[70%] md:basis-[28.5625rem] md:px-[0.34375rem]"
                            >
                                <Link
                                    to={category.link}
                                    className="group flex flex-col gap-3"
                                >
                                    <div className="overflow-hidden rounded-2.5">
                                        {/* The strip renders above the fold, so
                                            the cards in the initial viewport
                                            load eagerly at high priority (the
                                            grid-tile treatment); the cards
                                            beyond them stay lazy. */}
                                        <TileMedia
                                            aspectRatio="categoryCard"
                                            images={[category.image]}
                                            eager={index < eagerTileCount}
                                        />
                                    </div>
                                    {/* min-h reserves two title lines + count below md so the
                                        embla track height is content-invariant — the carousel→bar
                                        gap stays identical across verticals. Title and count hug;
                                        the slack sits below the count. */}
                                    <div className="flex flex-col max-md:min-h-[4.7rem]">
                                        <span
                                            className={cn(
                                                'text-[1.375rem] leading-[1.3] font-normal max-md:line-clamp-2',
                                                isDark
                                                    ? 'text-white'
                                                    : 'text-[#181818]',
                                            )}
                                        >
                                            {category.title}
                                        </span>
                                        <span
                                            className={cn(
                                                // 0.75rem literal — this repo's text-xs is 0.625rem
                                                'text-[1.25rem] leading-[1.875rem] max-md:text-[0.75rem] max-md:leading-[1.125rem]',
                                                isDark
                                                    ? 'text-white/50'
                                                    : 'text-[#707070]',
                                            )}
                                        >
                                            {formatToThousandsDelimiter(
                                                category.itemsCount ?? 0,
                                            )}{' '}
                                            {countLabel}
                                        </span>
                                    </div>
                                </Link>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>

                {/* No pagination chrome on mobile: swipe is the affordance and
                    autoplay never starts (the control's visibility gate is what
                    ticks the clock — with no control mounted, nothing advances,
                    so no pause affordance is required). Desktop keeps the
                    floating island. */}
            </ScrollReveal>
        </section>
    );
};
